import * as React from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

import { NewButton } from '../../../ui/NewButton/NewButton';
import styles from '../PdfRenderer.module.scss';

export interface DocumentSearchBarProps {
    query: string;
    onQueryChange: (value: string) => void;
    matchLabel: string;
    matchCount: number;
    onPrev: () => void;
    onNext: () => void;
    searching?: boolean;
    inputRef?: React.RefObject<HTMLInputElement | null>;
}

export function DocumentSearchBar({
    query,
    onQueryChange,
    matchLabel,
    matchCount,
    onPrev,
    onNext,
    searching = false,
    inputRef,
}: DocumentSearchBarProps) {
    const [open, setOpen] = React.useState(false);
    const [panelPos, setPanelPos] = React.useState({ top: 0, left: 0 });
    const triggerWrapRef = React.useRef<HTMLDivElement>(null);
    const panelRef = React.useRef<HTMLDivElement>(null);
    const localInputRef = React.useRef<HTMLInputElement>(null);

    const setInputRef = React.useCallback(
        (node: HTMLInputElement | null) => {
            localInputRef.current = node;
            if (inputRef) {
                (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
            }
        },
        [inputRef],
    );

    const updatePanelPos = React.useCallback(() => {
        const anchor = triggerWrapRef.current?.getBoundingClientRect();
        if (!anchor) return;
        const width = 320;
        const left = Math.min(
            Math.max(12, anchor.right - width),
            window.innerWidth - width - 12,
        );
        setPanelPos({ top: anchor.bottom + 8, left });
    }, []);

    const openPanel = React.useCallback(() => {
        updatePanelPos();
        setOpen(true);
        window.requestAnimationFrame(() => {
            localInputRef.current?.focus();
            localInputRef.current?.select();
        });
    }, [updatePanelPos]);

    const closePanel = React.useCallback(() => {
        setOpen(false);
    }, []);

    React.useEffect(() => {
        if (!open) return;

        updatePanelPos();
        const onResize = () => updatePanelPos();
        window.addEventListener('resize', onResize);
        window.addEventListener('scroll', onResize, true);

        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('scroll', onResize, true);
        };
    }, [open, updatePanelPos]);

    React.useEffect(() => {
        if (!open) return;

        const onPointerDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (panelRef.current?.contains(target)) return;
            if (triggerWrapRef.current?.contains(target)) return;
            closePanel();
        };

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            e.preventDefault();
            e.stopPropagation();
            closePanel();
        };

        document.addEventListener('mousedown', onPointerDown);
        // Capture: иначе Esc уходит в PrimeReact Dialog и закрывает весь просмотрщик.
        document.addEventListener('keydown', onKeyDown, true);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown, true);
        };
    }, [open, closePanel]);

    React.useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
                e.preventDefault();
                if (open) {
                    localInputRef.current?.focus();
                    localInputRef.current?.select();
                } else {
                    openPanel();
                }
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, openPanel]);

    const toggleOpen = () => {
        if (open) closePanel();
        else openPanel();
    };

    const hasQuery = query.trim().length > 0;

    return (
        <>
            <div ref={triggerWrapRef} className={styles.searchTriggerWrap}>
                <div className={styles.toolbarGroup}>
                    <NewButton
                        icon="fa-solid fa-magnifying-glass"
                        onClick={toggleOpen}
                        className={clsx(
                            styles.toolbarIconBtn,
                            (open || hasQuery) && styles['toolbarIconBtn--active'],
                        )}
                        tooltip="Поиск в документе (Ctrl+F)"
                        aria-label="Поиск в документе"
                        aria-expanded={open}
                        aria-haspopup="dialog"
                    />
                </div>
            </div>

            {open && createPortal(
                <div
                    ref={panelRef}
                    className={styles.searchPopover}
                    style={{ top: panelPos.top, left: panelPos.left }}
                    role="dialog"
                    aria-label="Поиск в документе"
                >
                    <div className={styles.searchPopoverHead}>
                        <span className={styles.searchPopoverTitle}>Поиск в документе</span>
                        <button
                            type="button"
                            className={styles.searchPopoverClose}
                            aria-label="Закрыть"
                            onClick={closePanel}
                        >
                            <i className="fa-solid fa-xmark" />
                        </button>
                    </div>

                    <div className={styles.searchPopoverField} role="search">
                        <i className={clsx('fa-solid fa-magnifying-glass', styles.searchPopoverFieldIcon)} aria-hidden />
                        <input
                            ref={setInputRef}
                            className={styles.searchPopoverInput}
                            type="search"
                            value={query}
                            onChange={(e) => onQueryChange(e.target.value)}
                            placeholder="Слово или фрагмент…"
                            aria-label="Строка поиска"
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    closePanel();
                                    return;
                                }
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (e.shiftKey) onPrev();
                                    else onNext();
                                }
                            }}
                        />
                    </div>

                    <div className={styles.searchPopoverFoot}>
                        <span className={styles.searchPopoverHint} aria-live="polite">
                            {searching ? 'Поиск…' : (matchCount > 0 ? matchLabel : (hasQuery ? 'Нет совпадений' : 'Enter — далее'))}
                        </span>
                        <div className={styles.searchPopoverNav}>
                            <NewButton
                                icon="fa-solid fa-chevron-up"
                                className={styles.toolbarIconBtn}
                                aria-label="Предыдущее совпадение"
                                onClick={onPrev}
                                disabled={matchCount === 0}
                            />
                            <NewButton
                                icon="fa-solid fa-chevron-down"
                                className={styles.toolbarIconBtn}
                                aria-label="Следующее совпадение"
                                onClick={onNext}
                                disabled={matchCount === 0}
                            />
                        </div>
                    </div>
                </div>,
                document.body,
            )}
        </>
    );
};
